function bindHeroSoundToggles(root) {
  if (!root || root.dataset.soundToggleBound === 'true') {
    return;
  }

  root.dataset.soundToggleBound = 'true';

  root.addEventListener('click', event => {
    const button = event.target.closest('.hero__sound-toggle');

    if (!button || !root.contains(button)) {
      return;
    }

    const media = button.closest('.hero__media');

    if (!media) {
      return;
    }

    const isMuted = media.dataset.muted !== 'false';
    const nextMuted = !isMuted;
    const mediaKind = media.dataset.mediaKind;

    if (mediaKind === 'native') {
      const video = media.querySelector('video');

      if (!video) {
        return;
      }

      video.muted = nextMuted;

      if (!nextMuted) {
        video.volume = 1;
        video.play().catch(() => {});
      }
    } else if (mediaKind === 'youtube') {
      const iframe = media.querySelector('.js-youtube');

      if (!iframe) {
        return;
      }

      iframe.contentWindow.postMessage(
        '{"event":"command","func":"' + (nextMuted ? 'mute' : 'unMute') + '","args":""}',
        '*'
      );

      if (!nextMuted) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        );
      }
    } else if (mediaKind === 'vimeo') {
      const iframe = media.querySelector('.js-vimeo');

      if (!iframe) {
        return;
      }

      iframe.contentWindow.postMessage(
        '{"method":"setVolume","value":' + (nextMuted ? 0 : 1) + '}',
        '*'
      );

      if (!nextMuted) {
        iframe.contentWindow.postMessage('{"method":"play"}', '*');
      }
    } else {
      return;
    }

    media.dataset.muted = `${nextMuted}`;
    button.setAttribute('aria-pressed', nextMuted ? 'false' : 'true');

    const label = button.querySelector('.hero__sound-toggle-label');

    if (label) {
      label.textContent = nextMuted ? 'Sound on' : 'Mute';
    }
  });
}

if (!customElements.get('hero-slider')) {
  class HeroSlider extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.sliderInitScheduled = false;
      this.sliderInitAttempts = 0;

      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleSlideChange = this.handleSlideChange.bind(this);

      if (Shopify.designMode) {
        window.addEventListener(
          'shopify:section:load',
          this.handleSectionLoad
        );

        window.addEventListener(
          'shopify:block:select',
          this.handleBlockSelect
        );
      }
    }

    connectedCallback() {
      const heroBanner = this.closest('.hero-banner');

      if (heroBanner) {
        bindHeroSoundToggles(heroBanner);
      }

      this.scheduleSliderInit();
    }

    disconnectedCallback() {
      if (Shopify.designMode) {
        window.removeEventListener(
          'shopify:section:load',
          this.handleSectionLoad
        );

        window.removeEventListener(
          'shopify:block:select',
          this.handleBlockSelect
        );
      }

      this.destroySlider();
    }

    scheduleSliderInit() {
      if (this.sliderInitScheduled || this.slider) {
        return;
      }

      this.sliderInitScheduled = true;

      const isMobile = window.matchMedia(
        '(max-width: 749px)'
      ).matches;

      const init = () => {
        this.sliderInitScheduled = false;

        if (!this.isConnected || this.slider) {
          return;
        }

        if (typeof Swiper === 'undefined') {
          this.sliderInitAttempts += 1;

          if (this.sliderInitAttempts <= 10) {
            window.setTimeout(() => {
              this.scheduleSliderInit();
            }, 100);

            return;
          }

          return;
        }

        this.sliderInitAttempts = 0;
        this.mountSlider();
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(init, {
          timeout: isMobile ? 1200 : 800
        });
      } else {
        window.setTimeout(
          init,
          isMobile ? 150 : 100
        );
      }
    }

    destroySlider() {
      if (this.slider && !this.slider.destroyed) {
        this.slider.destroy(true, true);
      }

      this.slider = null;
      this.sliderInitScheduled = false;
      this.sliderInitAttempts = 0;
    }

    handleSectionLoad(event) {
      const sectionId = event?.detail?.sectionId;
      const section = this.closest('section');

      if (
        sectionId &&
        section?.id !== `shopify-section-${sectionId}`
      ) {
        return;
      }

      if (!this.isConnected) {
        return;
      }

      this.reinitSlider();
    }

    handleBlockSelect(event) {
      if (!this.slider) {
        return;
      }

      const blockId = event?.detail?.blockId;
      let selectedSlideIndex = Number.NaN;

      if (blockId) {
        const selectedSlide = this.querySelector(
          `[data-block-id="${blockId}"]`
        );

        if (selectedSlide) {
          selectedSlideIndex = Number(
            selectedSlide.dataset.index
          );
        }
      }

      if (Number.isNaN(selectedSlideIndex)) {
        selectedSlideIndex = Number(
          event?.target?.dataset?.index
        );
      }

      if (Number.isNaN(selectedSlideIndex)) {
        return;
      }

      this.slider.slideTo(
        selectedSlideIndex,
        600
      );
    }

    reinitSlider() {
      this.destroySlider();
      this.scheduleSliderInit();
    }

    mountSlider() {
      if (
        this.slider &&
        !this.slider.destroyed
      ) {
        return;
      }

      if (typeof Swiper === 'undefined') {
        return;
      }

      const autoplayOptions = {
        delay: Number(
          this.dataset.autoplayInterval
        )
      };

      const paginationEl = this.querySelector(
        '.hero__swiper-pagination'
      );

      const nextEl = this.querySelector(
        '.hero__swiper-button--next'
      );

      const prevEl = this.querySelector(
        '.hero__swiper-button--prev'
      );

      const swiperOptions = {
        effect: 'fade',

        fadeEffect: {
          crossFade: true
        },

        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: true,

        autoplay:
          this.dataset.autoplay === 'true'
            ? autoplayOptions
            : false,

        on: {
          init: this.handleSlideChange,
          slideChange: this.handleSlideChange
        }
      };

      if (nextEl && prevEl) {
        swiperOptions.navigation = {
          nextEl,
          prevEl
        };
      }

      if (paginationEl) {
        swiperOptions.pagination = {
          el: paginationEl,
          clickable: true,

          renderBullet: function (
            i,
            className
          ) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>

                <svg
                  class="square-progress"
                  width="26"
                  height="26"
                >
                  <rect
                    class="square-origin"
                    width="26"
                    height="26"
                    rx="5"
                    ry="5"
                  />
                </svg>

                <svg
                  class="progress"
                  width="18"
                  height="18"
                  style="inset-inline-start: ${0 - ((i * 2.4) + 3.4)}rem"
                >
                  <circle
                    class="circle-origin"
                    r="8"
                    cx="9.5"
                    cy="9.5"
                  ></circle>
                </svg>
              </button>
            `;
          }
        };
      }

      this.slider = new Swiper(
        this,
        swiperOptions
      );
    }

    handleSlideChange(swiper) {
      const activeSlideIndex =
        typeof swiper.realIndex === 'number'
          ? swiper.realIndex
          : swiper.activeIndex;

      const activeSlide =
        swiper.slides?.[activeSlideIndex];

      const heroInner =
        activeSlide?.querySelector(
          '.hero__inner'
        );

      if (!heroInner) {
        return;
      }

      const headerMenuTextColor =
        heroInner.dataset
          .headerMenuTextColor;

      if (!headerMenuTextColor) {
        return;
      }

      document.documentElement.style.setProperty(
        '--transparent-header-menu-text-color',
        headerMenuTextColor
      );
    }
  }

  customElements.define(
    'hero-slider',
    HeroSlider
  );
}

document.querySelectorAll('.hero-banner').forEach(heroBanner => {
  if (!heroBanner.querySelector('hero-slider')) {
    bindHeroSoundToggles(heroBanner);
  }
});

if (Shopify.designMode) {
  window.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.hero-banner').forEach(heroBanner => {
      if (!heroBanner.querySelector('hero-slider')) {
        bindHeroSoundToggles(heroBanner);
      }
    });
  });
}
